import { defineNuxtPlugin } from 'nuxt/app'
import type { DrupalMessage } from '~/composables/useDrupalMessages'
import type { GraphqlResponseTyped } from '#nuxt-graphql-middleware/response'

type GraphqlMessengerMessage = {
  type: string
  message: string
  escaped: string
  safe: string
}

/**
 * Try to extract the messages from a GraphQL query or mutation.
 */
function extractMessages(data: GraphqlResponseTyped): DrupalMessage[] {
  if (data.data && 'messengerMessages' in data.data) {
    return data.data.messengerMessages.map((v: GraphqlMessengerMessage) => {
      return {
        type: v.type,
        message: v.safe,
      }
    })
  }

  return []
}

/**
 * This is only called when performing a query or mutation from within the nuxt
 * app (e.g. not via custom server routes).
 */
export default defineNuxtPlugin({
  name: 'zt:graphql-middleware',
  dependsOn: ['nuxt-graphql-middleware-provide-state'],
  setup() {
    const state = useGraphqlState()
    const { messages } = useDrupalMessages()

    if (!state) {
      return
    }

    const event = useRequestEvent()

    state.fetchOptions = {
      /**
       * Interceptor called whenever a GraphQL response arrives.
       */
      onResponse(result) {
        const data = result.response?._data
        if (import.meta.server) {
          useCDNHeaders((helper) => {
            if (!data?.extensions?.cacheability?.isCacheable) {
              helper.private()
              return
            }

            helper
              .public()
              .setNumeric('maxAge', data.extensions.cacheability.maxAge)
              .addTags(data.extensions.cacheability.tagsCdn)
          }, event)
        }

        if (!data) {
          // If we didn't receive any data, mark this response as uncacheable.
          useCDNHeaders((helper) => helper.private(), event)
          return
        }

        // Extract drupal messages from every GraphQL response.
        extractMessages(data).forEach((v) => {
          const exists = messages.value.find((m) => m.message === v.message)
          if (!exists) {
            messages.value.push(v)

            // When there are messages, we have to make the whole request uncacheable.
            useCDNHeaders((v) => v.private())
          }
        })
      },

      onRequest({ options, request }) {
        if (import.meta.server) {
          // Log queries in dev mode.
          if (import.meta.dev) {
            console.log('GraphQL Query: ' + request)
          }

          // Pass request headers to the GraphQL middleware.
          const headers = useRequestHeaders()

          for (const [key, value] of Object.entries(headers)) {
            options.headers.set(key, value)
          }
        }
      },
    }
  },
})
